import React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { useI18n } from '@ggs/i18n'

/**
 * @typedef {Object} ConfirmDialogProps
 * @property {boolean} open Flag that control the display of the dialog.
 * @property {function} cancelCallback Callback that triggers when action is cancelled.
 * @property {function} acceptCallback Callback that triggers when action is accepted.
 * @property {(event: React.MouseEvent<HTMLButtonElement>) => void} handleCloseClick Callback to trigger dialog close.
 * @property {string} acceptLabel Accept action button label.
 * @property {string} cancelLabel Cancel action button label.
 * @property {string} actionMessage Action confirmation message.
 */

/**
 * Confirmation dialog, on reject or accept will call the corresponding callback function.
 * @param {ConfirmDialogProps} props
 * @return {JSX.Element}
 */
const ConfirmDialog = ({
  open,
  handleCloseClick,
  cancelCallback,
  acceptCallback,
  acceptLabel,
  cancelLabel,
  actionMessage,
}) => {
  const { t } = useI18n()

  /**
   * Handle cancel button click.
   */
  const handleCancelClick = () => {
    cancelCallback()
  }

  /**
   * Handle accept button click.
   */
  const handleAcceptClick = () => {
    acceptCallback()
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleCloseClick}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('Please confirm your action')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{actionMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClick}>{cancelLabel}</Button>
          <Button onClick={handleAcceptClick} autoFocus>
            {acceptLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ConfirmDialog
